import React, { useContext, useMemo, useState } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import NtText from "../../../components/NtText";
import NtMaterialIcon from "../../../components/NtMaterialIcon";
import NtTouchableEffect from "../../../components/NtTouchableEffect";
import NtActivityIndicator from "../../../components/NtActivityIndicator";
import NtTextInput from "../../../components/NtTextInput";
import NtModalInputField from "../../../components/NtModalInputField";
import NtTabController from "../../../components/NtTabController";
import NtToggleRow from "../../../components/NtToggleRow";
import { useTheme } from "../../../context/ThemeContext";
import UserContext from "../../../context/UserContext";
import { proposePbxPlan } from "../../../api/anthropic";
import { applyPlan } from "../../utils/applyPlan";
import Logger from "../../../common/utils/Logger";

/**
 * AI-guided setup step. Flow:
 *   form  → loading → review → applying → done
 * From review the admin can apply the plan, refine it with free-form
 * feedback (loops back to loading → review with an updated plan), or start
 * over from the form. The wizard's outer Skip button still lets them bail
 * to the manual steps at any point.
 */
function NtSetupAssistantStep() {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const context = useContext(UserContext);
  const domain = context.user?.domain;

  const [phase, setPhase] = useState("form");
  const [mode, setMode] = useState("guided");
  const [businessType, setBusinessType] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [departments, setDepartments] = useState("");
  const [wantsIvr, setWantsIvr] = useState(true);
  const [notes, setNotes] = useState("");
  const [freeFormText, setFreeFormText] = useState("");

  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState([]);
  const [results, setResults] = useState(null);
  const [loadingLabel, setLoadingLabel] = useState("designing");
  const [feedbackText, setFeedbackText] = useState("");
  const [adjustingOpen, setAdjustingOpen] = useState(false);

  const canGenerate =
    mode === "guided"
      ? Boolean(businessType.trim() && teamSize.trim())
      : freeFormText.trim().length > 20;

  const currentProfile = () => {
    if (mode === "freeform") {
      return { freeFormDescription: freeFormText.trim() };
    }
    return {
      businessType: businessType.trim(),
      teamSize: teamSize.trim(),
      departments: departments.trim(),
      wantsIvr,
      notes: notes.trim(),
    };
  };

  const handleGenerate = async () => {
    setError(null);
    setLoadingLabel("designing");
    setPhase("loading");
    const res = await proposePbxPlan(currentProfile());
    Logger("Assistant · proposePbxPlan", res);
    if (!res.ok) {
      setError(res.error);
      setPhase("form");
      return;
    }
    setPlan(res.plan);
    setAdjustingOpen(false);
    setFeedbackText("");
    setPhase("review");
  };

  const handleRefine = async () => {
    const feedback = feedbackText.trim();
    if (!feedback || !plan) return;
    setError(null);
    setLoadingLabel("refining");
    setPhase("loading");
    const res = await proposePbxPlan(currentProfile(), {
      currentPlan: plan,
      feedback,
    });
    Logger("Assistant · proposePbxPlan (refine)", res);
    if (!res.ok) {
      setError(res.error);
      setPhase("review");
      return;
    }
    setPlan(res.plan);
    setAdjustingOpen(false);
    setFeedbackText("");
    setPhase("review");
  };

  const handleApply = async () => {
    setError(null);
    setProgress([]);
    setPhase("applying");
    const final = await applyPlan({
      domain,
      plan,
      onProgress: (evt) =>
        setProgress((prev) => {
          // Replace the latest matching pending row (same kind + label)
          // when it completes, otherwise append.
          const idx = prev.findIndex(
            (p) => p.kind === evt.kind && p.label === evt.label,
          );
          if (idx === -1) return [...prev, evt];
          const next = prev.slice();
          next[idx] = evt;
          return next;
        }),
    });
    Logger("Assistant · applyPlan results", final);
    setResults(final);
    setPhase("done");
  };

  const handleStartOver = () => {
    setPlan(null);
    setProgress([]);
    setResults(null);
    setError(null);
    setPhase("form");
  };

  if (phase === "loading") {
    const isRefining = loadingLabel === "refining";
    return (
      <View style={styles.centerWrap}>
        <View style={styles.centerInner}>
          <NtActivityIndicator color={theme.brand2} size="large" />
          <NtText style={styles.centerText}>
            {isRefining
              ? "Updating your plan…"
              : "Asking the AI to design your starter setup…"}
          </NtText>
          <NtText style={styles.centerSub}>
            {isRefining
              ? "Applying your feedback. Still nothing is saved — you'll review the updated plan first."
              : "Usually 5–15 seconds. We're not saving anything yet — you'll review the plan before anything gets created."}
          </NtText>
        </View>
      </View>
    );
  }

  if (phase === "review" && plan) {
    return (
      <ReviewView
        plan={plan}
        styles={styles}
        theme={theme}
        error={error}
        adjustingOpen={adjustingOpen}
        feedbackText={feedbackText}
        onOpenAdjust={() => {
          setAdjustingOpen(true);
          setError(null);
        }}
        onCancelAdjust={() => {
          setAdjustingOpen(false);
          setFeedbackText("");
        }}
        onFeedbackChange={setFeedbackText}
        onSubmitAdjust={handleRefine}
        onApply={handleApply}
        onStartOver={handleStartOver}
      />
    );
  }

  if (phase === "applying") {
    return (
      <View style={styles.applyWrap}>
        <NtText style={styles.sectionLabel}>Building your setup</NtText>
        <View style={styles.progressList}>
          {progress.map((p, i) => (
            <ProgressRow key={`${p.kind}-${p.label}-${i}`} item={p} theme={theme} />
          ))}
          {progress.length === 0 ? (
            <View style={styles.progressEmpty}>
              <NtActivityIndicator color={theme.brand2} size="small" />
              <NtText style={styles.progressEmptyText}>Starting…</NtText>
            </View>
          ) : null}
        </View>
      </View>
    );
  }

  if (phase === "done" && results) {
    return (
      <DoneView
        results={results}
        styles={styles}
        theme={theme}
        onStartOver={handleStartOver}
      />
    );
  }

  // form phase
  return (
    <ScrollView
      contentContainerStyle={styles.formScroll}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.formIntro}>
        <View style={styles.aiBadge}>
          <NtMaterialIcon name="robot" color={theme.brand2} size={14} />
          <NtText style={styles.aiBadgeText}>AI ASSISTED</NtText>
        </View>
        <NtText style={styles.formIntroText}>
          {mode === "guided"
            ? "Answer a few quick questions and the AI will suggest the extensions, ring groups, and IVR menu for your business. You'll review the plan before anything is created — and you can always skip and set things up manually."
            : "Describe your business and what you need from the phone system in your own words. The AI will figure out extensions, ring groups, and IVR menus from the description. You'll review the plan before anything is created."}
        </NtText>
      </View>

      <NtTabController
        containerStyle={{ marginTop: 14, alignSelf: "flex-start" }}
        options={MODE_OPTIONS}
        selection={MODE_OPTIONS.find((o) => o.id === mode)}
        onPress={(opt) => setMode(opt.id)}
      />

      {mode === "guided" ? (
        <>
          <NtModalInputField
            containerStyle={{ marginTop: 16 }}
            title="What kind of business is this? *"
            placeholder="e.g. small law firm, dental practice, e-commerce support"
            value={businessType}
            setValue={setBusinessType}
            footer="One sentence is plenty — the AI uses this to tailor extension naming and structure."
          />

          <NtModalInputField
            containerStyle={{ marginTop: 12 }}
            title="How many people will use the phone system? *"
            placeholder="e.g. 6"
            value={teamSize}
            setValue={(t) => setTeamSize(t.replace(/\D/g, ""))}
            keyboardType="numeric"
            footer="Including admins, support, sales, anyone who'll have an extension."
          />

          <NtModalInputField
            containerStyle={{ marginTop: 12 }}
            title="Departments or teams (optional)"
            placeholder="e.g. Sales, Support, Billing"
            value={departments}
            setValue={setDepartments}
            footer="Comma separated. Each department becomes a ring group."
          />

          <NtToggleRow
            containerStyle={{ marginTop: 14 }}
            value={wantsIvr}
            onChange={setWantsIvr}
            label="Include an auto-attendant (press 1 for…)"
            sub="Greets callers and lets them pick a department. Skip if you'd rather route every call straight to a person."
          />

          <NtModalInputField
            containerStyle={{ marginTop: 14 }}
            title="Anything else? (optional)"
            placeholder="e.g. after-hours should go to voicemail, we have two locations"
            value={notes}
            setValue={setNotes}
          />
        </>
      ) : (
        <View style={{ marginTop: 16 }}>
          <NtText style={styles.freeFormLabel}>
            Describe your phone-system needs *
          </NtText>
          <NtTextInput
            value={freeFormText}
            onChangeText={setFreeFormText}
            placeholder="e.g. We're a 12-person dental practice with a front desk, two hygienists, three dentists, and an office manager. Need an auto-attendant that lets callers reach the front desk, billing, or leave a message after hours…"
            multiline
            numberOfLines={6}
            containerStyle={styles.freeFormInput}
          />
          <NtText style={styles.freeFormHint}>
            The more context you give (team size, departments, after-hours
            handling), the better the proposed plan.
          </NtText>
        </View>
      )}

      {error ? (
        <View style={styles.errorBox}>
          <NtMaterialIcon
            name="alert-circle-outline"
            color={theme.red}
            size={14}
          />
          <NtText style={styles.errorText}>{error}</NtText>
        </View>
      ) : null}

      <NtTouchableEffect
        style={[styles.generateBtn, !canGenerate && styles.generateBtnDisabled]}
        onPress={canGenerate ? handleGenerate : null}
        disablePress={!canGenerate}
      >
        <NtMaterialIcon
          name="auto-fix"
          color={canGenerate ? "#fff" : theme.textMute}
          size={14}
        />
        <NtText
          style={[
            styles.generateBtnText,
            !canGenerate && { color: theme.textMute },
          ]}
        >
          Generate plan
        </NtText>
      </NtTouchableEffect>
    </ScrollView>
  );
}

const MODE_OPTIONS = [
  { id: "guided", title: "Guided questions" },
  { id: "freeform", title: "Describe it yourself" },
];

function ReviewView({
  plan,
  styles,
  theme,
  error,
  adjustingOpen,
  feedbackText,
  onOpenAdjust,
  onCancelAdjust,
  onFeedbackChange,
  onSubmitAdjust,
  onApply,
  onStartOver,
}) {
  return (
    <ScrollView
      contentContainerStyle={styles.reviewScroll}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.summaryBox}>
        <View style={styles.aiBadge}>
          <NtMaterialIcon name="robot" color={theme.brand2} size={14} />
          <NtText style={styles.aiBadgeText}>AI PLAN</NtText>
        </View>
        <NtText style={styles.summaryText}>{plan.summary}</NtText>
      </View>

      <PlanSection
        title="Extensions"
        count={(plan.extensions || []).length}
        styles={styles}
        theme={theme}
      >
        {(plan.extensions || []).map((e) => (
          <View key={e.extension} style={styles.planRow}>
            <View style={styles.planBadge}>
              <NtText style={styles.planBadgeText}>{e.extension}</NtText>
            </View>
            <View style={styles.planMid}>
              <NtText style={styles.planName}>{e.name}</NtText>
              {e.description ? (
                <NtText style={styles.planDesc} numberOfLines={1}>
                  {e.description}
                </NtText>
              ) : null}
            </View>
          </View>
        ))}
      </PlanSection>

      <PlanSection
        title="Ring groups"
        count={(plan.ringGroups || []).length}
        styles={styles}
        theme={theme}
      >
        {(plan.ringGroups || []).map((g) => (
          <View key={g.extension} style={styles.planRow}>
            <View style={[styles.planBadge, styles.planBadgeGroup]}>
              <NtText style={styles.planBadgeText}>{g.extension}</NtText>
            </View>
            <View style={styles.planMid}>
              <NtText style={styles.planName}>{g.name}</NtText>
              <NtText style={styles.planDesc} numberOfLines={1}>
                Rings: {(g.memberExtensions || []).join(", ")}
              </NtText>
            </View>
          </View>
        ))}
      </PlanSection>

      <PlanSection
        title="Auto-attendant"
        count={(plan.ivrs || []).length}
        styles={styles}
        theme={theme}
      >
        {(plan.ivrs || []).map((ivr) => (
          <View key={ivr.extension} style={styles.ivrCard}>
            <View style={styles.planRow}>
              <View style={[styles.planBadge, styles.planBadgeIvr]}>
                <NtText style={styles.planBadgeText}>{ivr.extension}</NtText>
              </View>
              <View style={styles.planMid}>
                <NtText style={styles.planName}>{ivr.name}</NtText>
                {ivr.greeting ? (
                  <NtText style={styles.planDesc} numberOfLines={2}>
                    "{ivr.greeting}"
                  </NtText>
                ) : null}
              </View>
            </View>
            <View style={styles.ivrOptions}>
              {(ivr.options || []).map((o) => (
                <View key={o.digit} style={styles.ivrOption}>
                  <View style={styles.ivrDigit}>
                    <NtText style={styles.ivrDigitText}>{o.digit}</NtText>
                  </View>
                  <NtText style={styles.ivrOptionLabel}>
                    {o.label}{" "}
                    <NtText style={styles.ivrOptionDest}>
                      → {o.destinationExtension}
                    </NtText>
                  </NtText>
                </View>
              ))}
            </View>
          </View>
        ))}
      </PlanSection>

      {adjustingOpen ? (
        <AdjustPanel
          styles={styles}
          theme={theme}
          value={feedbackText}
          onChange={onFeedbackChange}
          onCancel={onCancelAdjust}
          onSubmit={onSubmitAdjust}
        />
      ) : null}

      {error ? (
        <View style={styles.errorBox}>
          <NtMaterialIcon
            name="alert-circle-outline"
            color={theme.red}
            size={14}
          />
          <NtText style={styles.errorText}>{error}</NtText>
        </View>
      ) : null}

      <View style={styles.reviewActions}>
        <NtTouchableEffect style={styles.secondaryBtn} onPress={onStartOver}>
          <NtMaterialIcon name="refresh" color={theme.text} size={14} />
          <NtText style={styles.secondaryBtnText}>Try different answers</NtText>
        </NtTouchableEffect>
        {!adjustingOpen ? (
          <NtTouchableEffect style={styles.secondaryBtn} onPress={onOpenAdjust}>
            <NtMaterialIcon name="pencil" color={theme.text} size={14} />
            <NtText style={styles.secondaryBtnText}>Adjust this plan</NtText>
          </NtTouchableEffect>
        ) : null}
        <NtTouchableEffect style={styles.primaryBtn} onPress={onApply}>
          <NtMaterialIcon name="check" color="#fff" size={14} />
          <NtText style={styles.primaryBtnText}>Apply this plan</NtText>
        </NtTouchableEffect>
      </View>
    </ScrollView>
  );
}

function AdjustPanel({ styles, theme, value, onChange, onCancel, onSubmit }) {
  const canSubmit = value.trim().length > 0;
  return (
    <View style={styles.adjustPanel}>
      <View style={styles.adjustHeader}>
        <NtMaterialIcon name="pencil" color={theme.brand2} size={14} />
        <NtText style={styles.adjustTitle}>Tell the AI what to change</NtText>
      </View>
      <NtText style={styles.adjustHint}>
        Examples: "Start extensions at 100 instead of 1001", "Drop the IVR",
        "Sales should have 3 people not 2", "Add a Billing department".
      </NtText>
      <NtTextInput
        value={value}
        onChangeText={onChange}
        placeholder="Describe what should change…"
        multiline
        numberOfLines={4}
        containerStyle={styles.textareaContainer}
      />
      <View style={styles.adjustActions}>
        <NtTouchableEffect style={styles.secondaryBtn} onPress={onCancel}>
          <NtText style={styles.secondaryBtnText}>Cancel</NtText>
        </NtTouchableEffect>
        <NtTouchableEffect
          style={[styles.primaryBtn, !canSubmit && styles.primaryBtnDisabled]}
          onPress={canSubmit ? onSubmit : null}
          disablePress={!canSubmit}
        >
          <NtMaterialIcon
            name="auto-fix"
            color={canSubmit ? "#fff" : theme.textMute}
            size={14}
          />
          <NtText
            style={[
              styles.primaryBtnText,
              !canSubmit && { color: theme.textMute },
            ]}
          >
            Submit changes
          </NtText>
        </NtTouchableEffect>
      </View>
    </View>
  );
}

function PlanSection({ title, count, styles, theme, children }) {
  if (count === 0) return null;
  return (
    <View style={styles.planSection}>
      <View style={styles.planSectionHeader}>
        <NtText style={styles.planSectionTitle}>{title}</NtText>
        <View style={styles.countBadge}>
          <NtText style={styles.countBadgeText}>{count}</NtText>
        </View>
      </View>
      <View style={styles.planSectionBody}>{children}</View>
    </View>
  );
}

function ProgressRow({ item, theme }) {
  const color =
    item.status === "ok"
      ? theme.green
      : item.status === "fail"
      ? theme.red
      : theme.textMute;
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 6,
      }}
    >
      <View style={{ width: 18, alignItems: "center" }}>
        {item.status === "pending" ? (
          <NtActivityIndicator color={theme.brand2} size="small" />
        ) : (
          <NtMaterialIcon
            name={item.status === "ok" ? "check-circle" : "close-circle"}
            color={color}
            size={14}
          />
        )}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <NtText style={{ color: theme.text, fontSize: 12 }} numberOfLines={1}>
          {item.label}
        </NtText>
        {item.error ? (
          <NtText
            style={{ color: theme.red, fontSize: 11 }}
            numberOfLines={1}
          >
            {item.error}
          </NtText>
        ) : null}
      </View>
    </View>
  );
}

function DoneView({ results, styles, theme, onStartOver }) {
  const total =
    results.extensions.length +
    results.ringGroups.length +
    results.ivrs.length;
  const failed = results.failures.length;
  return (
    <ScrollView
      contentContainerStyle={styles.reviewScroll}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.doneBox}>
        <View style={styles.doneIco}>
          <NtMaterialIcon
            name={failed === 0 ? "check-circle-outline" : "alert-circle-outline"}
            color={failed === 0 ? theme.green : theme.orange || theme.red}
            size={28}
          />
        </View>
        <NtText style={styles.doneTitle}>
          {failed === 0
            ? "Your starter setup is live."
            : `Setup partially applied (${failed} item${failed === 1 ? "" : "s"} failed).`}
        </NtText>
        <NtText style={styles.doneSub}>
          Created {results.extensions.length} extensions,{" "}
          {results.ringGroups.length} ring groups, and{" "}
          {results.ivrs.length} IVR menus.
          {failed === 0
            ? " Click Continue to pick your phone number, or open the admin to fine-tune any of these."
            : " The wizard's other steps will show what made it through — finish there or use Continue to move on."}
        </NtText>
      </View>

      {failed > 0 ? (
        <View style={styles.failBox}>
          <NtText style={styles.failTitle}>Items that failed</NtText>
          {results.failures.map((f, i) => (
            <NtText key={i} style={styles.failItem}>
              · [{f.kind}] {f.item} — {f.error}
            </NtText>
          ))}
        </View>
      ) : null}

      <NtTouchableEffect style={styles.secondaryBtn} onPress={onStartOver}>
        <NtMaterialIcon name="refresh" color={theme.text} size={14} />
        <NtText style={styles.secondaryBtnText}>
          Start over with different answers
        </NtText>
      </NtTouchableEffect>
    </ScrollView>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    formScroll: { paddingBottom: 24, maxWidth: 720 },
    formIntro: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radiusCard,
      padding: 16,
    },
    aiBadge: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      alignSelf: "flex-start",
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      marginBottom: 8,
    },
    aiBadgeText: {
      color: t.brand2,
      fontSize: 10,
      fontWeight: "700",
      letterSpacing: 0.6,
    },
    formIntroText: { color: t.textDim, fontSize: 13, lineHeight: 19 },

    generateBtn: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      marginTop: 18,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: t.radius,
      backgroundColor: t.brand2,
      alignSelf: "flex-start",
    },
    generateBtnDisabled: {
      backgroundColor: t.surface3,
    },
    generateBtnText: { color: "#fff", fontSize: 13, fontWeight: "600" },

    errorBox: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 8,
      marginTop: 14,
      padding: 10,
      borderRadius: t.radius,
      borderWidth: 1,
      borderColor: "rgba(224,91,89,0.35)",
      backgroundColor: "rgba(224,91,89,0.08)",
    },
    errorText: { flex: 1, color: t.red, fontSize: 12, lineHeight: 18 },

    centerWrap: {
      flex: 1,
      alignSelf: "stretch",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 360,
      paddingVertical: 40,
    },
    centerInner: {
      alignItems: "center",
      justifyContent: "center",
      gap: 16,
      maxWidth: 480,
    },
    centerText: {
      color: t.text,
      fontSize: 15,
      fontWeight: "600",
      textAlign: "center",
    },
    centerSub: {
      color: t.textDim,
      fontSize: 12,
      textAlign: "center",
      lineHeight: 18,
    },

    reviewScroll: { paddingBottom: 24, gap: 12, maxWidth: 760 },
    summaryBox: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radiusCard,
      padding: 14,
    },
    summaryText: { color: t.text, fontSize: 13, lineHeight: 19 },

    planSection: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      overflow: "hidden",
    },
    planSectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
    },
    planSectionTitle: { color: t.text, fontSize: 12, fontWeight: "600" },
    planSectionBody: { padding: 8 },
    countBadge: {
      paddingHorizontal: 7,
      paddingVertical: 1,
      borderRadius: 999,
      backgroundColor: t.surface3,
      borderWidth: 1,
      borderColor: t.borderSoft,
    },
    countBadgeText: {
      color: t.textDim,
      fontSize: 10,
      fontWeight: "600",
    },
    planRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 8,
      paddingHorizontal: 6,
    },
    planBadge: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 4,
      backgroundColor: "rgba(37,112,189,0.15)",
      borderWidth: 1,
      borderColor: "rgba(37,112,189,0.30)",
    },
    planBadgeGroup: {
      backgroundColor: "rgba(76,183,130,0.15)",
      borderColor: "rgba(76,183,130,0.30)",
    },
    planBadgeIvr: {
      backgroundColor: "rgba(241,178,80,0.15)",
      borderColor: "rgba(241,178,80,0.30)",
    },
    planBadgeText: {
      color: t.brand2,
      fontSize: 11,
      fontWeight: "700",
      letterSpacing: 0.3,
    },
    planMid: { flex: 1, minWidth: 0 },
    planName: { color: t.text, fontSize: 13, fontWeight: "600" },
    planDesc: { color: t.textDim, fontSize: 11, marginTop: 1 },

    ivrCard: {
      paddingVertical: 4,
    },
    ivrOptions: {
      marginLeft: 38,
      marginTop: 4,
      paddingLeft: 10,
      borderLeftWidth: 2,
      borderLeftColor: t.borderSoft,
      gap: 4,
    },
    ivrOption: { flexDirection: "row", alignItems: "center", gap: 8 },
    ivrDigit: {
      width: 22,
      height: 22,
      borderRadius: 4,
      backgroundColor: t.surface3,
      alignItems: "center",
      justifyContent: "center",
    },
    ivrDigitText: { color: t.text, fontSize: 11, fontWeight: "700" },
    ivrOptionLabel: { color: t.text, fontSize: 12 },
    ivrOptionDest: { color: t.textDim, fontSize: 12 },

    reviewActions: {
      flexDirection: "row",
      gap: 10,
      marginTop: 8,
      flexWrap: "wrap",
    },
    primaryBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: t.radius,
      backgroundColor: t.brand2,
    },
    primaryBtnDisabled: { backgroundColor: t.surface3 },
    primaryBtnText: { color: "#fff", fontSize: 13, fontWeight: "600" },

    adjustPanel: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.pillBorder,
      borderRadius: t.radiusCard,
      padding: 14,
      gap: 10,
    },
    adjustHeader: { flexDirection: "row", alignItems: "center", gap: 6 },
    adjustTitle: { color: t.text, fontSize: 13, fontWeight: "600" },
    adjustHint: { color: t.textDim, fontSize: 12, lineHeight: 17 },
    textareaContainer: { minHeight: 90, alignItems: "flex-start" },

    freeFormLabel: {
      color: t.text,
      fontSize: 13,
      fontWeight: "600",
      marginBottom: 8,
    },
    freeFormInput: { minHeight: 140, alignItems: "flex-start" },
    freeFormHint: {
      color: t.textDim,
      fontSize: 11,
      marginTop: 6,
      lineHeight: 16,
    },
    adjustActions: { flexDirection: "row", gap: 10, flexWrap: "wrap" },
    secondaryBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderRadius: t.radius,
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
    },
    secondaryBtnText: { color: t.text, fontSize: 13, fontWeight: "600" },

    applyWrap: { maxWidth: 720 },
    sectionLabel: {
      fontSize: 11,
      fontWeight: "700",
      color: t.textMute,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      marginBottom: 14,
    },
    progressList: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      paddingHorizontal: 12,
      paddingVertical: 8,
    },
    progressEmpty: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 14,
    },
    progressEmptyText: { color: t.textDim, fontSize: 12 },

    doneBox: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radiusCard,
      padding: 18,
      alignItems: "flex-start",
    },
    doneIco: { marginBottom: 10 },
    doneTitle: {
      color: t.text,
      fontSize: 16,
      fontWeight: "700",
      marginBottom: 6,
    },
    doneSub: { color: t.textDim, fontSize: 13, lineHeight: 19 },

    failBox: {
      backgroundColor: "rgba(224,91,89,0.06)",
      borderWidth: 1,
      borderColor: "rgba(224,91,89,0.30)",
      borderRadius: t.radius,
      padding: 12,
    },
    failTitle: {
      color: t.red,
      fontSize: 12,
      fontWeight: "700",
      marginBottom: 6,
    },
    failItem: {
      color: t.textDim,
      fontSize: 11,
      marginTop: 2,
      lineHeight: 16,
    },
  });

export default NtSetupAssistantStep;
